import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Loader2, Send, MapPin } from 'lucide-react';
import { useVisibleTalent, useCreateConnectRequest, useMyConnectRequests, useShopHiringSettings } from '@/hooks/useConnectApi';
import { useAuth } from '@/contexts/AuthContext';

const ROLE_LABELS: Record<string, string> = {
  technician: 'Technician',
  advisor: 'Advisor',
  manager: 'Manager',
  student: 'Student',
};

const STATUS_LABELS: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  seeking: { label: 'Actively Seeking', variant: 'default' },
  quietly_open: { label: 'Quietly Open', variant: 'secondary' },
};

export function TalentDirectory() {
  const { activeShop } = useAuth();
  const navigate = useNavigate();
  const { data: hiringSettings } = useShopHiringSettings();

  const { data: allTalent, isLoading } = useVisibleTalent();
  const activeHiringRoles = hiringSettings?.hiring_roles ?? [];

  const talent = activeHiringRoles.length > 0
    ? allTalent?.filter((person) => {
        const personRoles = person.roles ?? [];
        return activeHiringRoles.some((role) => personRoles.includes(role));
      })
    : [];

  const { data: myRequests } = useMyConnectRequests();
  const sendRequest = useCreateConnectRequest();

  const isRecruiting = hiringSettings?.recruiting_enabled ?? false;

  const hasPendingTo = (userId: string) =>
    myRequests?.some(r => r.to_user_id === userId && r.status === 'pending');

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Passport Talent</CardTitle>
        </div>
        <CardDescription>Candidates who are open to work in your area.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active hiring roles (drives talent filtering) */}
        <div className="flex gap-2 flex-wrap">
          {activeHiringRoles.length > 0 ? (
            activeHiringRoles.map((role) => (
              <Badge key={role} variant="default">
                {ROLE_LABELS[role] || role}
              </Badge>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">
              Select one or more Hiring Roles above to see matching talent.
            </p>
          )}
        </div>

        {!isRecruiting && (
          <p className="text-sm text-warning bg-warning/10 rounded-md p-3">
            Turn on "Actively Hiring" above to contact candidates.
          </p>
        )}

        {!talent?.length ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No matching talent found.</p>
        ) : (
          <div className="space-y-3">
            {talent.map((person) => (
              <div key={person.user_id} className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
                    <Users className="h-4 w-4 text-primary" />
                  </div>
                  <div className="space-y-1.5">
                    {person.technician_profile_id ? (
                      <button
                        type="button"
                        className="text-sm font-medium text-primary hover:underline text-left"
                        onClick={() => navigate(`/technicians/${person.technician_profile_id}`)}
                      >
                        {person.display_name}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="text-sm font-medium text-primary hover:underline text-left"
                        onClick={() => navigate(`/connect/talent/${person.user_id}`)}
                      >
                        {person.display_name}
                      </button>
                    )}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {person.roles?.map((role) => (
                        <Badge key={role} variant="outline" className="text-xs">
                          {ROLE_LABELS[role] || role}
                        </Badge>
                      ))}
                      {person.open_to_work_status && STATUS_LABELS[person.open_to_work_status] && (
                        <Badge variant={STATUS_LABELS[person.open_to_work_status].variant} className="text-xs">
                          {STATUS_LABELS[person.open_to_work_status].label}
                        </Badge>
                      )}
                      {person.distance_miles != null && (
                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />
                          ~{person.distance_miles} mi
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={hasPendingTo(person.user_id) ? 'outline' : 'default'}
                  disabled={!isRecruiting || hasPendingTo(person.user_id) || sendRequest.isPending}
                  onClick={() =>
                    sendRequest.mutate({
                      from_type: 'shop',
                      from_shop_id: activeShop?.id,
                      to_type: 'user',
                      to_user_id: person.user_id,
                    })
                  }
                >
                  {hasPendingTo(person.user_id) ? 'Pending' : (
                    <>
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      Connect
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
